import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Sparkles } from "lucide-react";
import { ASSETS, COMPANY } from "@/lib/data";
import {
  TrustPointsRow,
  ServicesGrid,
  ReviewsSection,
  FAQSection,
  AreaMap,
  SectionLabel,
} from "@/components/Sections";
import QuoteForm from "@/components/QuoteForm";

export default function Home() {
  return (
    <>
      {/* HERO */}
      <section className="relative bg-white overflow-hidden" data-testid="home-hero">
        <div className="absolute inset-0 bg-grain opacity-60" />
        <div className="absolute -top-32 -right-32 w-[600px] h-[600px] rounded-full bg-[#F59E0B]/8 blur-3xl" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24 relative">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <SectionLabel className="mb-5">Lokal städfirma i nordvästra Skåne</SectionLabel>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-slate-900 leading-[1.05]">
                Professionell städning <span className="text-[#1E3A6F]">för företag och verksamheter</span>
              </h1>
              <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-xl">
                Rena Tjänster hjälper företag, fastigheter och verksamheter med kontorsstädning,
                byggstädning, butiksstädning, städning av arbetsbodar, golvvård och löpande
                städuppdrag i hela nordvästra Skåne.
              </p>
              <div className="mt-9 flex flex-wrap gap-3">
                <Link to="/kontakt#offertformular" data-testid="hero-cta-offert">
                  <Button className="bg-[#1E3A6F] hover:bg-[#152a51] text-white rounded-full px-8 py-6 font-medium transition-all hover:-translate-y-0.5">
                    Begär offert
                  </Button>
                </Link>
                <a href="#tjanster" data-testid="hero-cta-tjanster">
                  <Button variant="outline" className="border-2 border-[#1E3A6F] text-[#1E3A6F] hover:bg-slate-50 rounded-full px-8 py-6 font-medium">
                    Se våra tjänster <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </a>
              </div>
              <p className="mt-8 text-sm text-slate-500 flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#F59E0B]" />
                Vi utför våra tjänster dagligen i hela nordvästra Skåne.
              </p>
            </div>

            <div className="lg:col-span-5 relative">
              <div className="absolute -top-10 -right-6 w-40 h-40 rounded-full bg-[#1E3A6F]/8 blur-2xl" />
              <div className="relative rounded-3xl overflow-hidden shadow-2xl aspect-[4/5] border border-slate-100">
                <img src={ASSETS.heroTeam} alt="Rena Tjänsters bilar och personal i nordvästra Skåne" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1E3A6F]/40 via-transparent to-transparent" />
              </div>
              <div className="absolute -bottom-5 -left-5 bg-white rounded-2xl shadow-xl px-5 py-4 border border-slate-100 max-w-[220px]">
                <div className="flex items-center gap-3">
                  <div className="bg-[#F59E0B]/15 rounded-full p-2">
                    <Sparkles className="h-5 w-5 text-[#1E3A6F]" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Lokal & pålitlig</p>
                    <p className="font-display font-semibold text-slate-900 text-sm">På plats i hela Skåne</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TJÄNSTEKORT */}
      <div id="tjanster">
        <ServicesGrid title="Tjänster för företag och verksamheter" eyebrow="Våra tjänster" />
      </div>

      {/* TRYGGHETSPUNKTER */}
      <TrustPointsRow />

      {/* RECENSIONER */}
      <ReviewsSection />

      {/* ÅTERVÄNDANDE OMRÅDESKARTA */}
      <AreaMap />

      {/* FAQ */}
      <FAQSection />

      {/* KONTAKT / OFFERT */}
      <section className="py-20 lg:py-28 bg-[#F8FAFC]" data-testid="home-quote-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-5 gap-12">
          <div className="lg:col-span-2">
            <SectionLabel className="mb-4">Offertförfrågan</SectionLabel>
            <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900 mb-6">
              Berätta vad ni behöver hjälp med
            </h2>
            <p className="text-slate-600 leading-relaxed mb-8">
              Vi tar fram en kostnadsfri offert anpassad efter er verksamhet.
            </p>
            <div className="space-y-4">
              <div className="bg-white rounded-xl p-5 border border-slate-100">
                <p className="text-xs text-slate-500 mb-1">Adress</p>
                <p className="font-medium text-slate-900">{COMPANY.address}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <a href={`mailto:${COMPANY.email}`} className="bg-white rounded-xl p-5 border border-slate-100 hover:border-[#F59E0B]/40 transition-colors" data-testid="home-email-card">
                  <p className="text-xs text-slate-500 mb-1">E-post</p>
                  <p className="font-medium text-slate-900 text-sm truncate">{COMPANY.email}</p>
                </a>
                <a href={COMPANY.phoneLink} className="bg-white rounded-xl p-5 border border-slate-100 hover:border-[#F59E0B]/40 transition-colors" data-testid="home-phone-card">
                  <p className="text-xs text-slate-500 mb-1">Telefon</p>
                  <p className="font-medium text-slate-900">{COMPANY.phone}</p>
                </a>
              </div>
            </div>
          </div>
          <div className="lg:col-span-3">
            <QuoteForm />
          </div>
        </div>
      </section>
    </>
  );
}
